import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollTo } from "./Nav";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=2400&q=85&fm=jpg";

const Line = ({ children, delay, className }) => (
  <div className="overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative isolate min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Footballer under stadium lights"
          className="w-full h-[120%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-black/50" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-40 pb-10 md:pb-14">
        <div className="overflow-hidden mb-6">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="font-jetbrains text-xs md:text-sm uppercase tracking-[0.35em] text-volt"
            data-testid="hero-kicker"
          >
            {t("hero.kicker")}
          </motion.p>
        </div>

        <h1 className="font-cabinet font-black uppercase leading-[0.88] tracking-tight" data-testid="hero-heading">
          <Line delay={0.35} className="text-[15vw] md:text-[11vw] text-volt">{t("hero.l1")}</Line>
          <Line delay={0.5} className="text-[8vw] md:text-[5.5vw] mt-2">{t("hero.l2")}</Line>
          <Line delay={0.65} className="text-[8vw] md:text-[5.5vw]">
            {t("hero.l3a")}<span className="text-outline">{t("hero.l3b")}</span>
          </Line>
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="hero-cta-primary"
              className="group rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 flex items-center gap-2 hover:bg-white transition-colors duration-300"
            >
              {t("hero.cta1")}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollTo("#founders")}
              data-testid="hero-cta-secondary"
              className="rounded-full border border-white/25 text-white/80 font-cabinet font-bold uppercase tracking-wide px-8 py-4 hover:border-volt hover:text-volt transition-colors duration-300"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-12 md:mt-16 pt-6 border-t border-white/10 flex items-center justify-between"
        >
          <p className="font-jetbrains text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/40" data-testid="hero-statline">
            {t("hero.statline")}
          </p>
          <ArrowDown size={18} className="text-volt scroll-hint" />
        </motion.div>
      </motion.div>
    </section>
  );
}
